// @flow

import { bubaClientV2 } from "buba-components/src/api";
import endpointsV2 from "buba-components/src/api/endpointsV2";
import { enhance, authenticate as buildAuthenticate } from "buba-components/src/api/enhancers";

import { store } from "../store";
import type { InquiryStatus } from "../data/types/inquiries";

const authenticate = buildAuthenticate(store);

export const getInquiryV2 = (payload: { id: number }) =>
  bubaClientV2(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpointsV2.loanProcess(payload.id)
    })
  );

export const getBankAdvisorInquiriesV2 = (payload: {
  count_by_status?: boolean,
  status: InquiryStatus,
  page?: number,
  per_page?: number,
  sortBy?: string,
  sortDirection?: "asc" | "desc"
}) => {
  const apiPayload = {
    count_by_status: payload.count_by_status,
    "filters[mapped_status_eq]": payload.status,
    page: payload.page,
    per_page: payload.per_page,
    ...(payload.sortBy && payload.sortDirection
      ? {
          [`sort[${payload.sortBy}]`]: payload.sortDirection
        }
      : {})
  };

  return bubaClientV2(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpointsV2.bankAdvisorInquiries(),
      params: apiPayload
    })
  );
};

export const getDashboardActionsV2 = (payload: { id: number }) =>
  bubaClientV2(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpointsV2.getDashboardActions(payload.id)
    })
  );

export const getFileRequestsV2 = (payload: { id: number }) =>
  bubaClientV2(
    enhance(authenticate)({
      url: endpointsV2.getFileRequests(payload.id)
    })
  );

export const downloadFileV2 = (id: string, fileId: string) =>
  bubaClientV2(
    enhance(authenticate)({
      url: endpointsV2.file(id, fileId),
      headers: {
        Accept: "application/pdf"
      },
      responseType: "arraybuffer"
    })
  );

export const downloadAllFilesV2 = (payload: { id: number }) =>
  bubaClientV2(
    enhance(authenticate)({
      url: endpointsV2.files(String(payload.id)),
      headers: {
        Accept: "application/zip, application/pdf"
      },
      responseType: "arraybuffer"
    })
  );

export const uploadFileV2 = (payload: {
  id: number,
  file: File,
  file_upload_request_id?: number,
  recaptchaValue: string
}) => {
  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("recaptcha_value", payload.recaptchaValue);

  if (payload.file_upload_request_id) {
    formData.append("file_request_id", payload.file_upload_request_id);
  }

  return bubaClientV2(
    enhance(authenticate)({
      validateStatus: status => status === 201,
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/vnd.api+json"
      },
      data: formData,
      url: endpointsV2.uploadFile(payload.id)
    })
  );
};

export const uploadAndCheckFileV2 = (payload: {
  id: number,
  file: File,
  file_upload_request_id?: number,
  recaptchaValue: string
}) => uploadFileV2(payload);

export const downloadSummaryPdfV2 = (id: string) =>
  bubaClientV2(
    enhance(authenticate)({
      url: endpointsV2.downloadSummaryPdf(id),
      headers: {
        Accept: "application/pdf"
      },
      responseType: "arraybuffer"
    })
  );

export const setCreatorFeedbackV2 = (payload: {
  id: number,
  creator_feedback: string,
  creator_feedback_details: string
}) => {
  const { id, ...payloadWithoutId } = payload;
  return bubaClientV2(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "put",
      url: endpointsV2.setCreatorFeedback(payload.id),
      data: payloadWithoutId
    })
  );
};

export const getInquiryFidesDataV2 = (payload: { id: number }) =>
  bubaClientV2(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpointsV2.inquiryFidesData(payload.id)
    })
  );

export const downloadGuaranteeCertificateV2 = (id: string) =>
  bubaClientV2(
    enhance(authenticate)({
      url: endpointsV2.downloadGuaranteeCertificate(id),
      headers: {
        Accept: "application/pdf, application/zip"
      },
      responseType: "arraybuffer"
    })
  );
